import { Application, ColorMatrixFilter, Container, Sprite, Texture } from "pixi.js";
import { AdvancedBloomFilter } from "pixi-filters";
import { createFalloffTexture } from "./falloff";
import { createSpectrumBackground } from "./spectrumBackground";
import { WaveField } from "./WaveField";

export interface StageHandles {
  app: Application;
  linesLayer: Container;     // holds the spectral-line meshes
  waveLayer: Container;      // holds the wave-field graphics (behind lines, in bloom)
  waveField: WaveField;
  background: Sprite;
  falloffTex: Texture;
  bloom: AdvancedBloomFilter;
  brightness: ColorMatrixFilter;
  width: number;
  height: number;
}

export async function createStage(mount: HTMLElement): Promise<StageHandles> {
  const app = new Application();
  await app.init({
    // Force WebGL so our custom GlProgram-based wave shader runs everywhere.
    preference: "webgl",
    resizeTo: mount,
    antialias: true,
    backgroundColor: 0x000000,
    resolution: Math.min(window.devicePixelRatio || 1, 2),
    autoDensity: true,
  });
  mount.appendChild(app.canvas);

  const brightness = new ColorMatrixFilter();
  brightness.brightness(1.0, false);

  // Cheap bloom: fewer Kawase passes (quality) and a smaller blur radius keep
  // it light on phones. The threshold is retained so only the bright touched
  // line glows — the dim spectrum background stays below it and doesn't wash out.
  const bloom = new AdvancedBloomFilter({
    threshold: 0.2,
    bloomScale: 1.05,
    brightness: 1.0,
    blur: 4,
    quality: 3,
  });

  const background = createSpectrumBackground(app.screen.width, app.screen.height);

  // Single bloomed parent holds the wave layer (back) AND the lines layer
  // (front). Doing it this way guarantees both go through the same filter
  // sub-pixel framing, so ripples line up exactly with the lines they
  // emanate from. Visual z-order keeps ripples in the background.
  const bloomedParent = new Container();
  bloomedParent.filters = [brightness, bloom];
  bloomedParent.filterArea = app.screen;

  const waveLayer = new Container();
  const linesLayer = new Container();
  // background is the bottom-most child so it's painted into the same filter
  // texture as the lines and ripples. Placing it outside bloomedParent caused
  // it to be overwritten: the WaveField mesh writes alpha=1 to every pixel of
  // the filter RT, and Pixi's source-over composite step then hid the sprite.
  bloomedParent.addChild(background);
  bloomedParent.addChild(waveLayer);
  bloomedParent.addChild(linesLayer);

  app.stage.addChild(bloomedParent);

  const falloffTex = createFalloffTexture();
  const waveField = new WaveField(waveLayer, app.screen.width, app.screen.height);

  return {
    app,
    linesLayer,
    waveLayer,
    waveField,
    background,
    falloffTex,
    bloom,
    brightness,
    // The CURRENT stage size, not merely the initial one, even though this is
    // where it starts. Anything built after boot reads it — Line bakes its
    // whole mesh against these two numbers — so main.ts's renderer 'resize'
    // handler writes them on every resize. Keep that handler and this pair
    // together: they were not, once, and changing the element after a rotation
    // rebuilt the spectrum at the size the phone had when the page loaded.
    width: app.screen.width,
    height: app.screen.height,
  };
}
